import React from 'react';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { cn } from '../lib/utils';
import type { PostMedia } from '../types/database';

interface MediaGalleryProps {
  media: PostMedia[];
  onMediaClick: (index: number) => void;
  fullscreen?: boolean;
  currentIndex?: number;
  onClose?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

export default function MediaGallery({ 
  media, 
  onMediaClick,
  fullscreen = false,
  currentIndex = 0,
  onClose,
  onPrevious,
  onNext
}: MediaGalleryProps) {
  const hasMultiple = media.length > 1;

  const getVideoUrl = (url: string) => {
    return `${url}#t=0.001`;
  };

  if (fullscreen) {
    const currentMedia = media[currentIndex];

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors p-2 rounded-full hover:bg-white/10 z-[60]"
        >
          <X className="h-8 w-8" />
        </button>

        {hasMultiple && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrevious?.();
              }}
              className={cn(
                "absolute left-4 h-24 px-6 flex items-center justify-center z-[60]",
                "text-white hover:text-white transition-colors",
                "bg-black/50 hover:bg-black/75 rounded-xl",
                "transform hover:scale-105 transition-transform",
                currentIndex === 0 && "opacity-50 cursor-not-allowed hover:scale-100"
              )}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="h-12 w-12" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext?.();
              }}
              className={cn(
                "absolute right-4 h-24 px-6 flex items-center justify-center z-[60]",
                "text-white hover:text-white transition-colors",
                "bg-black/50 hover:bg-black/75 rounded-xl",
                "transform hover:scale-105 transition-transform",
                currentIndex === media.length - 1 && "opacity-50 cursor-not-allowed hover:scale-100"
              )}
              disabled={currentIndex === media.length - 1}
            >
              <ChevronRight className="h-12 w-12" />
            </button>
          </>
        )}

        <div className="relative max-w-[90vw] max-h-[90vh] z-[55]" onClick={(e) => e.stopPropagation()}>
          {currentMedia.type === 'image' ? (
            <img
              src={currentMedia.url}
              alt={`Media ${currentIndex + 1} of ${media.length}`}
              className="max-w-full max-h-[90vh] object-contain"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.src = `https://via.placeholder.com/800x600?text=Image+Not+Available`;
              }}
            />
          ) : (
            <video
              src={getVideoUrl(currentMedia.url)}
              controls
              autoPlay
              className="max-w-full max-h-[90vh]"
              onError={(e) => {
                const video = e.target as HTMLVideoElement;
                video.poster = `https://via.placeholder.com/800x600?text=Video+Not+Available`;
                video.controls = false;
              }}
            />
          )}

          {hasMultiple && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-[60]">
              {media.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMediaClick(index);
                  }}
                  className={cn(
                    "w-2.5 h-2.5 rounded-full transition-colors",
                    index === currentIndex ? "bg-white" : "bg-white/50 hover:bg-white/75"
                  )}
                  aria-label={`Go to media ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Grid layout for thumbnail view
  return (
    <div className="relative rounded-lg overflow-hidden">
      <div className={cn(
        "grid gap-1",
        media.length === 1 && "grid-cols-1",
        media.length === 2 && "grid-cols-2",
        media.length >= 3 && "grid-cols-2",
        "aspect-[16/9]"
      )}>
        {/* First image spans two rows when there are 3 or more images */}
        {media.length > 0 && (
          <div
            className={cn(
              "relative cursor-pointer overflow-hidden h-full group",
              media.length >= 3 && "row-span-2"
            )}
            onClick={() => onMediaClick(0)}
          >
            {media[0].type === 'image' ? (
              <img
                src={media[0].url}
                alt={`Media 1`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://via.placeholder.com/800x600?text=Image+Not+Available`;
                }}
              />
            ) : (
              <div className="relative w-full h-full">
                <video
                  src={getVideoUrl(media[0].url)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const video = e.target as HTMLVideoElement;
                    video.poster = `https://via.placeholder.com/800x600?text=Video+Not+Available`;
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity">
                  <div className="w-12 h-12 rounded-full bg-white bg-opacity-80 flex items-center justify-center">
                    <Play className="h-6 w-6 text-black" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Second image */}
        {media.length >= 2 && (
          <div
            className="relative cursor-pointer overflow-hidden h-full group"
            onClick={() => onMediaClick(1)}
          >
            {media[1].type === 'image' ? (
              <img
                src={media[1].url}
                alt={`Media 2`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://via.placeholder.com/800x600?text=Image+Not+Available`;
                }}
              />
            ) : (
              <div className="relative w-full h-full">
                <video
                  src={getVideoUrl(media[1].url)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const video = e.target as HTMLVideoElement;
                    video.poster = `https://via.placeholder.com/800x600?text=Video+Not+Available`;
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity">
                  <div className="w-12 h-12 rounded-full bg-white bg-opacity-80 flex items-center justify-center">
                    <Play className="h-6 w-6 text-black" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Third image or remaining count */}
        {media.length >= 3 && (
          <div
            className="relative cursor-pointer overflow-hidden h-full group"
            onClick={() => onMediaClick(2)}
          >
            {media[2].type === 'image' ? (
              <img
                src={media[2].url}
                alt={`Media 3`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://via.placeholder.com/800x600?text=Image+Not+Available`;
                }}
              />
            ) : (
              <div className="relative w-full h-full">
                <video
                  src={getVideoUrl(media[2].url)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const video = e.target as HTMLVideoElement;
                    video.poster = `https://via.placeholder.com/800x600?text=Video+Not+Available`;
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity">
                  <div className="w-12 h-12 rounded-full bg-white bg-opacity-80 flex items-center justify-center">
                    <Play className="h-6 w-6 text-black" />
                  </div>
                </div>
              </div>
            )}

            {/* Show remaining count if there are more than 3 images */}
            {media.length > 3 && (
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  +{media.length - 3}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}